<template>
  <!-- Treasure Refining Modal -->
  <div v-if="gameState.inRefiningTreasure" class="treasure-modal" @click="closeModal">
    <div class="treasure-card" @click.stop>
      <!-- Fixed Header -->
      <div class="treasure-header">
        <h3>炼器炉</h3>
        <p>选择法宝配方，放入材料进行炼制。</p>
      </div>

      <!-- Scrollable Content -->
      <div class="treasure-content">
        <!-- Treasure Selection -->
        <div v-if="!gameState.refiningItemName" class="selection-section">
          <h4>选择要炼制的法宝</h4>
          <div class="treasure-list">
            <button
              v-for="treasure in refinableTreasures"
              :key="treasure"
              @click="selectTreasure(treasure)"
              class="treasure-button"
            >
              {{ treasure }}
            </button>
          </div>
        </div>

        <!-- Recipe Display -->
        <div v-else class="recipe-section">
          <div class="recipe-header">
            <h4>选择配方：{{ gameState.refiningItemName }}</h4>
            <div class="recipe-controls">
              <button @click="backToSelection" class="back-btn">返回选择</button>
              <button @click="autoFillMaterials" class="auto-fill-btn" :disabled="isProcessing">
                {{ autoFillText }}
              </button>
              <button @click="refineTreasure" class="refine-btn" :disabled="!canRefine || isProcessing">
                {{ refineBtnText }}
              </button>
            </div>
          </div>

          <h4>所需材料</h4>
          <div class="recipe-materials">
            <div v-for="ingredient in recipe.ingredients" :key="ingredient.item" class="material-item">
              <span class="material-name">{{ ingredient.item }}</span>
              <span class="material-required">需要: {{ ingredient.count }}</span>
              <span class="material-owned">拥有: {{ ownedMaterials[ingredient.item] || 0 }}</span>
              <div class="material-inputs">
                <input
                  v-model.number="selectedMaterials[ingredient.item]"
                  type="number"
                  min="0"
                  :max="ownedMaterials[ingredient.item] || 0"
                  placeholder="0"
                  class="material-input"
                />
                <button @click="addMaterial(ingredient.item, 1)" class="add-btn">+</button>
                <button @click="removeMaterial(ingredient.item, 1)" class="remove-btn">-</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Status Messages -->
        <div v-if="statusMessage" class="status-message">
          {{ statusMessage }}
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="treasure-footer">
        <button @click="closeModal" class="close-btn">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { refineTreasure, listRefinableTreasures, getTreasureRecipe, getAllTreasureRecipes } from '../treasures';

export default {
  name: 'TreasureRefining',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedMaterials: {},
      statusMessage: '',
      isProcessing: false,
      isRefining: false,
      autoFillText: '一键放入',
      refineBtnText: '开始炼器',
      timerId: null
    };
  },
  computed: {
    recipes() {
      return getAllTreasureRecipes();
    },
    recipe() {
      return getTreasureRecipe(this.gameState.refiningItemName) || { ingredients: [] };
    },
    ownedMaterials() {
      return this.gameState.bag || {};
    },
    canRefine() {
      // Check materials
      for (const ingredient of this.recipe.ingredients || []) {
        const selected = this.selectedMaterials[ingredient.item] || 0;
        if (selected < ingredient.count) return false;
      }
      // 取消技能检查，允许任何玩家炼制
      return true;
    },
    refinableTreasures() {
      return listRefinableTreasures();
    }
  },
  watch: {
    'gameState.refiningItemName': function() {
      this.resetSelection();
    }
  },
  methods: {
    resetSelection() {
      this.selectedMaterials = {};
      this.statusMessage = '';
    },
    addMaterial(material, count) {
      const current = this.selectedMaterials[material] || 0;
      const required = this.getRequiredCount(material);
      const owned = this.ownedMaterials[material] || 0;
      const maxSelectable = Math.min(required, owned);
      this.$set(this.selectedMaterials, material, Math.min(current + count, maxSelectable));
    },
    removeMaterial(material, count) {
      const current = this.selectedMaterials[material] || 0;
      this.$set(this.selectedMaterials, material, Math.max(current - count, 0));
    },
    getRequiredCount(material) {
      const ingredient = this.recipe.ingredients?.find(ing => ing.item === material);
      return ingredient ? ingredient.count : 0;
    },
    async autoFillMaterials() {
      this.autoFillText = '已放入';
      this.isProcessing = true;

      for (const ingredient of this.recipe.ingredients || []) {
        const owned = this.ownedMaterials[ingredient.item] || 0;
        this.$set(this.selectedMaterials, ingredient.item, Math.min(ingredient.count, owned));
      }

      // 短暂延迟后恢复按钮状态
      setTimeout(() => {
        this.autoFillText = '一键放入';
        this.isProcessing = false;
      }, 500);
    },
    // 开始炼器
    async refineTreasure() {
      if (!this.canRefine || this.isProcessing) return;

      this.isRefining = true;
      this.isProcessing = true;

      const endTime = Date.now() + this.recipe.refiningTime;
      this.startRefiningTimer(endTime);
    },

    // 炼器定时器
    startRefiningTimer(endTime) {
      this.timerId = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
        this.refineBtnText = remaining + '秒';

        if (remaining <= 0) {
          this.completeRefining();
        }
      }, 1000);
    },

    // 完成炼器
    completeRefining() {
      clearInterval(this.timerId);
      this.timerId = null;

      this.consumeMaterials();
      this.performRefining('炼器');
      this.resetRefiningState();
    },

    // 消耗材料
    consumeMaterials() {
      for (const [material, count] of Object.entries(this.selectedMaterials)) {
        if (count > 0) {
          this.gameState.bag[material] -= count;
          if (this.gameState.bag[material] <= 0) {
            delete this.gameState.bag[material];
          }
        }
      }
    },

    // 执行炼器逻辑
    performRefining(type) {
      const success = Math.random() < this.recipe.successRate;

      if (success) {
        this.handleRefiningSuccess(type);
      } else {
        this.handleRefiningFailure(type);
      }
    },

    // 炼器成功处理
    handleRefiningSuccess(type) {
      if (!this.gameState.bag[this.gameState.refiningItemName]) {
        this.gameState.bag[this.gameState.refiningItemName] = 0;
      }
      this.gameState.bag[this.gameState.refiningItemName] += 1;

      // 增加熟练度
      if (!this.gameState.skills[this.recipe.skillRequired]) {
        this.gameState.skills[this.recipe.skillRequired] = 0;
      }
      this.gameState.skills[this.recipe.skillRequired] += this.recipe.proficiencyGain;

      this.statusMessage = `${type}成功！获得 ${this.gameState.refiningItemName} 一件。${type}熟练度 +${this.recipe.proficiencyGain}`;
      console.log(this.statusMessage);
    },

    // 炼器失败处理
    handleRefiningFailure(type) {
      this.statusMessage = `${type}失败，材料尽失。`;
      console.log(this.statusMessage);
    },

    // 重置炼器状态
    resetRefiningState() {
      this.$emit('refine-result', this.statusMessage);
      this.refineBtnText = '开始炼器';
      this.isProcessing = false;
      this.isRefining = false;
      this.resetSelection();
    },
    selectTreasure(treasure) {
      this.gameState.refiningItemName = treasure;
    },
    backToSelection() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
        this.isRefining = false;
        this.isProcessing = false;
        this.refineBtnText = '开始炼器';
      }
      this.gameState.refiningItemName = '';
      this.resetSelection();
    },
    closeModal() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
        this.isRefining = false;
        this.isProcessing = false;
      }
      this.gameState.inRefiningTreasure = false;
      this.gameState.refiningItemName = '';
      this.resetSelection();
    }
  }
};
</script>

<style scoped>
.treasure-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.treasure-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 400px;
  max-width: 600px;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.treasure-header {
  flex-shrink: 0;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #334155;
}

.treasure-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.treasure-footer {
  flex-shrink: 0;
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: center;
}

.treasure-card h3 {
  margin: 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
  border-bottom: 1px solid #334155;
  padding-bottom: 8px;
}

.treasure-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
}

.recipe-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.recipe-header h4 {
  margin: 0;
  font-size: 14px;
  color: #34d399;
}

.recipe-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.recipe-section h4 {
  margin: 8px 0 4px 0;
  font-size: 12px;
  color: #34d399;
}

.material-item {
  margin: 3px 0;
  color: #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.material-name {
  font-weight: bold;
  color: #dbeafe;
  flex: 1;
  font-size: 13px;
}

.material-required,
.material-owned {
  font-size: 12px;
  color: #94a3b8;
  margin-right: 6px;
}

.material-inputs {
  display: flex;
  align-items: center;
  gap: 3px;
}

.material-input {
  width: 45px;
  padding: 3px 2px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #dbeafe;
  font-family: monospace;
  font-size: 12px;
  text-align: center;
  border-radius: 2px;
}

.add-btn,
.remove-btn {
  padding: 3px 5px;
  border: none;
  background: #334155;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 11px;
  border-radius: 2px;
  min-width: 20px;
}

.auto-fill-btn {
  padding: 4px 8px;
  border: 1px solid #ca8a04;
  background: transparent;
  color: #ca8a04;
  cursor: pointer;
  font-size: 10px;
  font-family: monospace;
  border-radius: 4px;
}

.auto-fill-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refine-btn {
  padding: 4px 8px;
  border: 1px solid #16a34a;
  background: transparent;
  color: #16a34a;
  cursor: pointer;
  font-size: 10px;
  font-family: monospace;
  border-radius: 4px;
}

.refine-btn:disabled {
  border-color: #475569;
  color: #64748b;
  cursor: not-allowed;
}

.status-message {
  margin-top: 8px;
  padding: 6px;
  background: #1e293b;
  border: 1px solid #334155;
  font-size: 11px;
  color: #ffd166;
  border-radius: 4px;
}

.selection-section h4 {
  margin: 8px 0 4px 0;
  font-size: 12px;
  color: #34d399;
}

.treasure-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.treasure-button {
  padding: 8px 12px;
  border: 1px solid #475569;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 11px;
  font-family: monospace;
  text-align: left;
  border-radius: 6px;
  transition: all 0.2s;
}

.treasure-button:hover {
  background: rgba(96, 165, 250, 0.1);
  border-color: #60a5fa;
  color: #dbeafe;
}

.back-btn {
  padding: 4px 8px;
  border: 1px solid #6b7280;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 10px;
  font-family: monospace;
  border-radius: 4px;
}

.back-btn:hover {
  background: #334155;
}

.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

/* 适配移动设备 */
@media (max-width: 900px) {
  .treasure-card {
    min-width: 90vw;
  }

  .treasure-header {
    padding: 16px 16px 12px 16px;
  }

  .treasure-content {
    padding: 12px 16px;
  }

  .treasure-footer {
    padding: 12px 16px 16px 16px;
  }

  .material-item {
    display: block;
    padding: 8px 10px;
  }

  .material-item > :first-child,
  .material-item > :nth-child(2),
  .material-item > :nth-child(3) {
    display: inline-block;
    margin-bottom: 6px;
  }

  .material-item > :first-child {
    margin-right: 12px;
  }

  .material-item > :nth-child(2) {
    margin-right: 8px;
  }

  .material-item > :last-child {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 8px;
  }

  .material-name {
    font-size: 14px;
  }

  .material-required,
  .material-owned {
    font-size: 13px;
  }

  .material-input {
    width: 50px;
    padding: 4px 2px;
    font-size: 13px;
  }

  .add-btn,
  .remove-btn {
    padding: 4px 6px;
    font-size: 12px;
    min-width: 24px;
  }

  .recipe-controls {
    flex-direction: column;
    gap: 4px;
  }

  .auto-fill-btn,
  .refine-btn {
    flex: 1;
    text-align: center;
  }
}
</style>
